import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Trophy, Medal, Award } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface LeaderboardEntry {
  user_id: string;
  display_name: string;
  completed_days: number;
  completion_percentage: number;
  profiles?: {
    display_name: string;
    avatar_url: string | null;
  };
}

interface ChallengeLeaderboardProps {
  challengeId: string;
  challengeTitle: string;
}

export function ChallengeLeaderboard({ challengeId, challengeTitle }: ChallengeLeaderboardProps) {
  const { data: leaderboard, isLoading } = useQuery({
    queryKey: ['challenge-leaderboard', challengeId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('challenge_participants')
        .select('user_id, progress_data')
        .eq('challenge_id', challengeId)
        .in('completion_status', ['active', 'completed']);

      if (error) throw error;

      // Get user profiles
      const userIds = data.map(p => p.user_id);
      const { data: profiles } = await supabase
        .from('profiles')
        .select('user_id, display_name, avatar_url')
        .in('user_id', userIds);

      const profileMap = new Map(profiles?.map(p => [p.user_id, p]));

      // Calculate leaderboard
      const entries: LeaderboardEntry[] = data
        .map(participant => {
          const progressData = participant.progress_data as any;
          const completedDays = progressData?.completed_days || 0;
          const totalDays = progressData?.total_logged_days || 0;
          
          return {
            user_id: participant.user_id,
            display_name: profileMap.get(participant.user_id)?.display_name || 'Unknown',
            completed_days: completedDays,
            completion_percentage: totalDays > 0 ? (completedDays / totalDays) * 100 : 0,
            profiles: profileMap.get(participant.user_id),
          };
        })
        .sort((a, b) => {
          // Sort by completed days first, then by percentage
          if (b.completed_days !== a.completed_days) {
            return b.completed_days - a.completed_days;
          }
          return b.completion_percentage - a.completion_percentage;
        });

      return entries;
    },
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <p className="text-sm text-muted-foreground">Loading leaderboard...</p>
        </CardContent>
      </Card>
    );
  }

  if (!leaderboard || leaderboard.length === 0) {
    return null;
  }

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Trophy className="h-5 w-5 text-primary" />;
    if (rank === 2) return <Medal className="h-5 w-5 text-muted-foreground" />;
    if (rank === 3) return <Award className="h-5 w-5 text-secondary" />;
    return null;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Trophy className="h-5 w-5" />
          {challengeTitle} - Leaderboard
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {leaderboard.slice(0, 10).map((entry, index) => (
            <div
              key={entry.user_id}
              className="flex items-center gap-3 p-3 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
            >
              <div className="flex items-center gap-3 flex-1">
                <div className="flex items-center justify-center w-8 h-8">
                  {getRankIcon(index + 1) || (
                    <span className="text-sm font-medium text-muted-foreground">
                      #{index + 1}
                    </span>
                  )}
                </div>
                <Avatar className="h-10 w-10">
                  <AvatarFallback>
                    {entry.display_name?.[0]?.toUpperCase() || 'U'}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <p className="font-medium">{entry.display_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {entry.completed_days} days completed
                  </p>
                </div>
              </div>
              <Badge variant={index < 3 ? "default" : "secondary"}>
                {Math.round(entry.completion_percentage)}%
              </Badge>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
